import { api } from '../../../../../lib/api';

function fileNameOf(file) {
  return file?.orgFileNm || file?.fileNm || file?.name || '첨부파일';
}

function fileSizeText(file) {
  const size = Number(file?.fileSize || 0);
  if (!size) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  if (size >= 1024) return `${Math.round(size / 1024)}KB`;
  return `${size}B`;
}

function mapBoardNode(item) {
  return {
    id: item.boardUid,
    label: item.boardNm,
    boardUid: item.boardUid,
    parentBoardUid: item.parentBoardUid ?? null,
    boardGroupCd: item.boardGroupCd,
    boardKindCd: item.boardKindCd,
    boardTpCd: item.boardTpCd,
    boardDesc: item.boardDesc || '',
    sortOrd: Number(item.sortOrd || 0),
    useYn: item.useYn || 'Y',
    isActive: String(item.useYn || 'Y').toUpperCase() !== 'N',
    children: Array.isArray(item.children) ? item.children.map(mapBoardNode) : [],
  };
}

function mapPostListItem(item) {
  return {
    id: item.postUid,
    title: item.title || '',
    summary: '',
    replies: Number(item.commentCnt || 0),
    views: Number(item.viewCnt || 0),
    writtenAt: item.regDt || '',
    authorInitial: (item.regNm || '').charAt(0),
    authorName: item.regNm || '',
    org: '',
    regUid: item.regUid,
    regId: item.regId || '',
    fileUid: item.fileUid || null,
  };
}

function mapPostDetail(item) {
  return {
    tags: [],
    body: item?.content || '',
    fileUid: item?.fileUid || null,
    attachments: Array.isArray(item?.files)
      ? item.files.map((file) => ({
          id: file.fileId || file.fileUid,
          name: fileNameOf(file),
          size: fileSizeText(file),
          fileId: file.fileId,
          fileUid: file.fileUid,
          filePath: file.filePath,
          fileType: file.fileType,
          fileNm: file.fileNm,
          orgFileNm: file.orgFileNm,
          fileSize: file.fileSize,
          raw: file,
        }))
      : [],
    responsesCount: Number(item?.commentCnt || 0),
  };
}

/** Alliance 게시글 댓글 목록 페이지 크기 (서버 페이지네이션·CommentThread 기본값과 맞출 것) */
export const ALLIANCE_COMMENT_PAGE_SIZE = 5;

function mapComment(item) {
  return {
    id: String(item.commentUid),
    writer: item.regNm || '',
    author: {
      regUid: item.regUid,
      name: item.regNm || '',
      org: '',
      userId: item.regId || '',
    },
    regUid: item.regUid,
    userId: item.regId || '',
    parentCommentUid: item.parentCommentUid ?? null,
    parentId: item.parentCommentUid ?? null,
    date: item.regDt || '',
    content: item.content || '',
    raw: item,
  };
}

export async function fetchAllianceBoards(spaceUid, boardGroupCd) {
  const res = await api.get(`/api/alli/spaces/${spaceUid}/boards`, {
    params: { boardGroupCd },
  });
  return Array.isArray(res) ? res.map(mapBoardNode) : [];
}

/** Overview 등 — 해당 스페이스 과제(프로젝트) 정보(TASK_INFO) + 협업게시판/자료실(COOPERATION) 게시글 누적 건수 */
export async function fetchAllianceSpaceBoardPostSummary(spaceUid) {
  const res = await api.get(`/api/alli/spaces/${spaceUid}/board-post-summary`);
  return {
    taskInfoCount: Number(res?.taskInfoCount ?? 0),
    cooperationCount: Number(res?.cooperationCount ?? 0),
    totalCount: Number(res?.totalCount ?? 0),
  };
}

function mapFeedPostRow(item) {
  return {
    id: item.postUid,
    postUid: item.postUid,
    boardUid: item.boardUid,
    boardGroupCd: item.boardGroupCd || '',
    boardNm: item.boardNm || '',
    title: item.title || '',
    authorName: item.regNm || '',
    regId: item.regId || '',
    regDt: item.regDt || '',
    views: Number(item.viewCnt ?? 0),
  };
}

/** 과제(TASK_INFO) + 협업(COOPERATION) 게시판 통합 최신글 — 서버 페이징(Page: list, totalElements, totalPages, number, size) */
export async function fetchAllianceSpaceBoardPostFeed(spaceUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? 10;
  const query = { pageNumber, size };
  const rawSearch = params.search != null ? String(params.search).trim() : '';
  if (rawSearch) {
    query.search = rawSearch;
    const st = params.searchTp || params.searchType || 'title';
    query.searchTp = st === 'author' ? 'writer' : st;
  }
  const groupCd = params.boardGroupCd != null ? String(params.boardGroupCd).trim().toUpperCase() : '';
  if (groupCd === 'TASK_INFO' || groupCd === 'COOPERATION') {
    query.boardGroupCd = groupCd;
  }
  const res = await api.get(`/api/alli/spaces/${spaceUid}/board-post-feed`, { params: query });
  const list = Array.isArray(res?.list) ? res.list : [];
  const totalElements = Number(res?.totalElements ?? res?.totalCount ?? list.length);
  const totalPages = Math.max(0, Number(res?.totalPages ?? 0));
  const number = Number(res?.number ?? pageNumber);
  return {
    list,
    items: list.map(mapFeedPostRow),
    totalElements,
    totalPages,
    number,
    size: Number(res?.size ?? size),
    totalCount: totalElements,
  };
}

export async function createAllianceBoard(spaceUid, payload) {
  return api.post(`/api/alli/spaces/${spaceUid}/boards`, payload);
}

export async function updateAllianceBoard(spaceUid, boardUid, payload) {
  return api.put(`/api/alli/spaces/${spaceUid}/boards/${boardUid}`, payload);
}

export async function deleteAllianceBoard(spaceUid, boardUid) {
  return api.delete(`/api/alli/spaces/${spaceUid}/boards/${boardUid}`);
}

/** Alliance 게시글 목록 — 서버 페이징(Page: list, totalElements, totalPages, number, size). 쿼리는 pageNumber(1-based), size */
export async function fetchAlliancePosts(spaceUid, boardUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? 10;
  const query = { pageNumber, size };
  const rawSearch = params.search != null ? String(params.search).trim() : '';
  if (rawSearch) {
    query.search = rawSearch;
    const st = params.searchTp || params.searchType || 'title';
    query.searchTp = st === 'author' ? 'writer' : st;
  }
  const res = await api.get(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts`, { params: query });
  const list = Array.isArray(res?.list) ? res.list : [];
  const totalElements = Number(res?.totalElements ?? res?.totalCount ?? list.length);
  const totalPages = Math.max(0, Number(res?.totalPages ?? 0));
  const number = Number(res?.number ?? pageNumber);
  return {
    list,
    items: list.map(mapPostListItem),
    totalElements,
    totalPages,
    number,
    size: Number(res?.size ?? size),
    totalCount: totalElements,
  };
}

/** 댓글 목록 — 서버 페이징(Page: list, totalElements, totalPages, number, size). 정렬: 최신순(REG_DT DESC) */
export async function fetchAlliancePostCommentsPage(spaceUid, boardUid, postUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? ALLIANCE_COMMENT_PAGE_SIZE;
  const sortDir = params.sortDir;
  const res = await api.get(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}/comments`, {
    params: { pageNumber, size, ...(sortDir ? { sortDir } : {}) },
  });
  const list = Array.isArray(res?.list) ? res.list : [];
  return {
    items: list.map(mapComment),
    totalElements: Number(res?.totalElements ?? 0),
    totalPages: Math.max(0, Number(res?.totalPages ?? 0)),
    number: Number(res?.number ?? pageNumber),
    size: Number(res?.size ?? size),
  };
}

/** 같은 글 상세가 동시에 여러 번 요청되면(React Strict Mode, 이펙트 재실행 등) 조회수가 중복 증가하므로 in-flight 를 공유 */
const inflightAlliancePostDetail = new Map();

export async function fetchAlliancePostDetail(spaceUid, boardUid, postUid) {
  const key = `${spaceUid}:${boardUid}:${postUid}`;
  const existing = inflightAlliancePostDetail.get(key);
  if (existing) {
    return existing;
  }
  const promise = (async () => {
    const item = await api.get(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}`);
    return {
      item,
      post: mapPostListItem(item),
      detail: mapPostDetail(item),
      comments: [],
    };
  })();
  inflightAlliancePostDetail.set(key, promise);
  promise.finally(() => {
    inflightAlliancePostDetail.delete(key);
  });
  return promise;
}

export async function saveAlliancePost(spaceUid, boardUid, payload, postUid = null) {
  if (postUid) {
    await api.put(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}`, payload);
    return postUid;
  }
  return api.post(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts`, payload);
}

export async function deleteAlliancePost(spaceUid, boardUid, postUid) {
  return api.delete(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}`);
}

export async function createAllianceComment(spaceUid, boardUid, postUid, payload) {
  return api.post(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}/comments`, payload);
}

export async function updateAllianceComment(spaceUid, boardUid, postUid, commentUid, payload) {
  return api.put(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}/comments/${commentUid}`, payload);
}

export async function deleteAllianceComment(spaceUid, boardUid, postUid, commentUid) {
  return api.delete(`/api/alli/spaces/${spaceUid}/boards/${boardUid}/posts/${postUid}/comments/${commentUid}`);
}
